<template>
  <div class="app-container">
    <!-- Login page (no layout) -->
    <router-view v-if="isLoginPage" />

    <!-- Main application layout (with sidebar) -->
    <div v-else class="app-layout">
      <!-- Mobile overlay when sidebar is open -->
      <div
        v-if="mobileMenuOpen"
        class="mobile-overlay"
        @click="closeMobileMenu"
      ></div>

      <Sidebar :mobileMenuOpen="mobileMenuOpen" @close-mobile-menu="closeMobileMenu" />

      <div class="main-content" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
        <ImpersonationBanner v-if="authStore.isAuthenticated" />
        <TopBar @toggle-mobile-menu="toggleMobileMenu" />
        <div class="content-area">
          <router-view />
        </div>
      </div>

      <!-- Bottom Navigation (mobile only) -->
      <BottomNav @open-menu="toggleMobileMenu" />

      <!-- Loading overlay (shown on top of layout, doesn't unmount sidebar) -->
      <div v-if="authStore.loading" class="auth-loading-overlay">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>

    <!-- Toast notifications -->
    <ToastContainer />

    <!-- PWA Update Prompt -->
    <PWAUpdatePrompt />

    <!-- Install App Prompt (mobile only) -->
    <InstallAppPrompt />
  </div>
</template>

<script setup>
import { computed, provide, ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useSettingsStore } from '@/stores/settings';
import Sidebar from '@/components/layout/Sidebar.vue';
import TopBar from '@/components/layout/TopBar.vue';
import BottomNav from '@/components/layout/BottomNav.vue';
import ImpersonationBanner from '@/components/shared/ImpersonationBanner.vue';
import ToastContainer from '@/components/shared/ToastContainer.vue';
import PWAUpdatePrompt from '@/components/shared/PWAUpdatePrompt.vue';
import InstallAppPrompt from '@/components/shared/InstallAppPrompt.vue';

const route = useRoute();
const authStore = useAuthStore();
const settingsStore = useSettingsStore();

const sidebarCollapsed = ref(false);
const mobileMenuOpen = ref(false);
const isMobile = ref(false);

// Check if current route is login page
const isLoginPage = computed(() => route.name === 'login');

// Provide sidebar state to child components
provide('sidebarCollapsed', sidebarCollapsed);
provide('isMobile', isMobile);

// Mobile menu functions
function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

function closeMobileMenu() {
  mobileMenuOpen.value = false;
}

// Check screen size
function checkScreenSize() {
  isMobile.value = window.innerWidth < 768;
  // Close mobile menu if screen becomes larger
  if (!isMobile.value) {
    mobileMenuOpen.value = false;
  }
  // Update body class for global CSS targeting
  document.body.classList.toggle('is-mobile', isMobile.value);
  document.body.classList.toggle('is-desktop', !isMobile.value);
}

// Close mobile menu on route change
watch(() => route.path, () => {
  closeMobileMenu();
});

// Set body class immediately (before mount) to prevent flash of wrong styles
checkScreenSize();

onMounted(() => {
  checkScreenSize();
  window.addEventListener('resize', checkScreenSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkScreenSize);
});

// Load settings when user is authenticated
watch(() => authStore.isAuthenticated, (isAuth) => {
  if (isAuth) {
    settingsStore.loadSettings();
  }
}, { immediate: true });
</script>

<style>
/* Import Bootstrap CSS */
@import 'bootstrap/dist/css/bootstrap.min.css';
@import 'bootstrap-icons/font/bootstrap-icons.css';

/* Import custom styles */
@import './assets/styles/main.css';
</style>

<style scoped>
.app-container {
  height: 100vh;
  width: 100vw;
}

.app-layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.auth-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
  z-index: 9999;
}

.main-content {
  flex: 1;
  margin-left: 250px;
  overflow-y: auto; /* Scroll container for overall page */
  transition: margin-left 0.3s ease;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Allow flex child to shrink */
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

.content-area {
  flex: 1;
  min-height: 0; /* Required for nested flex overflow to work */
  padding: 30px;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Mobile overlay */
.mobile-overlay {
  display: none;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 60px; /* Account for fixed TopBar height */
  }

  .main-content.sidebar-collapsed {
    margin-left: 0;
  }

  .content-area {
    padding: 15px;
    padding-bottom: calc(70px + env(safe-area-inset-bottom));
  }

  .mobile-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
}

@media (max-width: 576px) {
  .content-area {
    padding: 10px;
    padding-bottom: calc(70px + env(safe-area-inset-bottom));
  }
}
</style>
